import { format } from 'd3-format';
import type {
  GraphCoordinate,
  GraphGeometry,
  GraphLine,
  GraphOptions,
  CircleCoordinate,
  GraphPoint,
  IndexedValueWithDisplay,
} from '../index.ts';
import { parsedString } from './stringUtils.ts';

const HOVER_MARGIN = 6; //in SVG standard pixels
function xScaleValue(x: number, geometry: GraphGeometry): number {
  return geometry.xScale.invert(x);
}

function getClosestDataX(
  cursorXCoordinate: number,
  data: GraphLine[],
  geometry: GraphGeometry,
  dataRight?: GraphLine[],
): number {
  let cursorX = xScaleValue(cursorXCoordinate, geometry);
  // Limit cursor to graph data range.
  if (cursorX < geometry.limits.xmin) {
    cursorX = geometry.limits.xmin;
  } else if (cursorX > geometry.limits.xmax) {
    cursorX = geometry.limits.xmax;
  }
  let x = findClosestXInGraphLines(cursorX, data, geometry);
  if (dataRight) {
    const x2 = findClosestXInGraphLines(cursorX, dataRight, geometry);
    if (Math.abs(x - cursorX) > Math.abs(x2 - cursorX)) {
      x = x2;
    }
  }
  return x;
}

function findClosestXInGraphLines(cursorX: number, data: GraphLine[], geometry: GraphGeometry) {
  let offset = 1e60;
  for (const line of data) {
    if (line && typeof line[0] !== 'undefined' && typeof line[line.length - 1] !== 'undefined') {
      const leftmostPoint = line[0];
      const leftmostPointX = leftmostPoint[0] as number; //Covered by guard clause from caller
      // scale domain value to coordinates, subtract the hover margin, scale result back to a domain value.
      const leftmostMarginX = xScaleValue(geometry.xScale(leftmostPointX) - HOVER_MARGIN, geometry);
      const rightmostPoint = line[line.length - 1] as GraphPoint; //Covered by guard clause above
      const rightmostPointX = rightmostPoint[0] as number; //Covered by guard clause from caller
      const rightmostMarginX = xScaleValue(geometry.xScale(rightmostPointX) + HOVER_MARGIN, geometry);
      if (line.length > 0 && leftmostMarginX <= cursorX && rightmostMarginX >= cursorX) {
        // Find the point on the line that is closest to the cursor X value.
        if (Math.abs(leftmostPointX - cursorX) < Math.abs(rightmostPointX - cursorX)) {
          // Go left to right - we'll finish sooner
          let lineOffset = leftmostPointX - cursorX;
          for (let pointIndex = 1; pointIndex < line.length; pointIndex++) {
            const nextPoint = line[pointIndex];
            if (nextPoint && typeof nextPoint[0] !== 'undefined') {
              const nextPointX = nextPoint[0];
              if (Math.abs(nextPointX - cursorX) < Math.abs(lineOffset)) {
                lineOffset = nextPointX - cursorX;
              }
              // X values are continuously increasing. If we're getting further away, we can leave the loop
              const previousPoint = line[pointIndex - 1];
              if (previousPoint && typeof previousPoint[0] !== 'undefined') {
                const previousPointX = previousPoint[0];
                if (Math.abs(nextPointX - cursorX) > Math.abs(previousPointX - cursorX)) {
                  break;
                }
              }
            }
          }
          // Discover which line's closest point is closest of all.
          if (Math.abs(lineOffset) < Math.abs(offset)) {
            offset = lineOffset;
          }
        } else {
          // Go right to left - we'll finish sooner
          let lineOffset = rightmostPointX - cursorX;
          for (let pointIndex = line.length - 1; pointIndex > 0; pointIndex--) {
            const nextPoint = line[pointIndex];
            if (nextPoint && typeof nextPoint[0] !== 'undefined') {
              const nextPointX = nextPoint[0];
              if (Math.abs(nextPointX - cursorX) < Math.abs(lineOffset)) {
                lineOffset = nextPointX - cursorX;
              }
              // Going backwards, X values are continuously decreasing. If we're getting further away, we can leave the loop
              const previousPoint = line[pointIndex - 1];
              if (previousPoint && typeof previousPoint[0] !== 'undefined') {
                const previousPointX = previousPoint[0];
                if (Math.abs(nextPointX - cursorX) < Math.abs(previousPointX - cursorX)) {
                  break;
                }
              }
            }
          }
          // Discover which line's closest point is closest of all.
          if (Math.abs(lineOffset) < Math.abs(offset)) {
            offset = lineOffset;
          }
        }
      }
    }
  }
  return offset === 1e60 ? cursorX : cursorX + offset;
}

function getXValues(xValue: number, options: GraphOptions): { formattedValue: string; formattedValueWithUnit: string } {
  const xFormatter = options.xformat
    ? format(options.xformat)
    : options.xscale === 'log'
      ? format('.2s')
      : format('.1f');
  const formattedValue = xFormatter(xValue);
  const formattedValueWithUnit = parsedString(xFormatter(xValue)).concat(
    options.xUnit === undefined ? '' : options.xUnit,
  );
  return {
    formattedValue: formattedValue,
    formattedValueWithUnit: formattedValueWithUnit,
  };
}

function getYValues(
  xValue: number,
  options: GraphOptions,
  cursor: GraphPoint,
  data: GraphLine[],
  geometry: GraphGeometry,
  dataRight?: GraphLine[],
  geometryRight?: GraphGeometry,
): IndexedValueWithDisplay[] {
  let yValues = data.map((graphLine, index) => {
    const isSpur = options.hasSpurs ? (options.hasSpurs[index] ?? false) : false;
    const isLog = options.yscale === 'log';
    return getYValue(xValue, data, graphLine, geometry, cursor, index, isSpur, isLog, options.yformat, options.yUnit);
  });
  if (options.yAxes === 2 && dataRight && geometryRight) {
    const yValuesRight = dataRight.map((graphLine, i) => {
      const index = yValues.length + i;
      const isSpur = options.hasSpurs ? (options.hasSpurs[index] ?? false) : false;
      const isLog = options.yscale2 === 'log';
      return getYValue(
        xValue,
        dataRight,
        graphLine,
        geometryRight,
        cursor,
        index,
        isSpur,
        isLog,
        options.yformat2,
        options.yUnit2,
      );
    });
    yValues = yValues.concat(yValuesRight);
  }
  const result1 = yValues.filter((obj) => {
    return obj.yCoordinate !== null;
  }) as IndexedValueWithDisplay[];
  const result = result1.sort((a, b) => {
    return a.yCoordinate > b.yCoordinate ? 1 : a.yCoordinate == b.yCoordinate ? 0 : -1;
  });
  return result;
}

function getCircleCoordinates(
  xValue: number,
  options: GraphOptions,
  geometry: GraphGeometry,
  lines: GraphLine[],
  dataRight?: GraphLine[],
  geometryRight?: GraphGeometry,
): CircleCoordinate[] {
  const coords = lines.map((graphLine, index) => {
    const isSpur = options.hasSpurs?.[index] ?? false;
    const isLog = options.yscale === 'log';
    return circleCoordinate(xValue, graphLine, index, geometry, isSpur, isLog);
  });
  if (options.yAxes === 2 && dataRight && geometryRight) {
    const coordsRight = dataRight.map((graphLine, index) => {
      const isSpur = options.hasSpurs?.[lines.length + index] ?? false;
      const isLog = options.yscale2 === 'log';
      return circleCoordinate(xValue, graphLine, index, geometryRight, isSpur, isLog);
    });
    return coords.concat(coordsRight);
  } else {
    return coords;
  }
}

function getYValue(
  xValue: number,
  data: GraphLine[],
  line: GraphLine,
  geometry: GraphGeometry,
  cursor: GraphPoint,
  index: number,
  isSpur: boolean,
  isLog: boolean,
  yFormat?: string,
  yUnit?: string,
) {
  const graphLine = line;
  const isFocused = function (cursorY: number, yCoordinate: number | null): boolean {
    if (yCoordinate == null) return false;
    return cursorY >= yCoordinate - HOVER_MARGIN && cursorY <= yCoordinate + HOVER_MARGIN;
  };
  const y = isSpur ? checkSpurGraphY(xValue, graphLine, geometry) : interpolateGraphY(xValue, graphLine, isLog);
  const yFormatter = yFormat ? format(yFormat) : isLog ? format('.2s') : format('.1f');
  let yCoordinate: number | null = null;
  if (isLog && y === 0) {
    yCoordinate = 100000;
  } else if (y !== null && typeof geometry.yScale(y) !== 'undefined') {
    yCoordinate = geometry.yScale(y);
  }
  const yDisplay = y !== null && Math.abs(y) < 1e-25 ? 0 : y; // Display zero for values smaller than < 0.1 yoctoUnits
  const displayValue =
    yDisplay === null ? '' : parsedString(yFormatter(yDisplay)).concat(yUnit === undefined ? '' : yUnit);
  let focused = false;
  if (cursor && typeof cursor[1] !== 'undefined') {
    focused = isFocused(cursor[1], yCoordinate);
  } else {
    throw 'cursor Y-coordinate not found';
  }
  return {
    index: index,
    yCoordinate: yCoordinate,
    displayValue: displayValue,
    focused: focused,
  };
}

function circleCoordinate(
  x: number,
  graphLine: GraphLine,
  index: number,
  geometry: GraphGeometry,
  isSpur: boolean,
  isLogGraph: boolean,
): CircleCoordinate {
  let showCircle = false;
  let graph: GraphCoordinate = { x: 0, y: 0 };
  const y = isSpur ? checkSpurGraphY(x, graphLine, geometry) : interpolateGraphY(x, graphLine, isLogGraph);
  showCircle =
    x >= geometry.limits.xmin &&
    x <= geometry.limits.xmax &&
    y !== null &&
    y >= geometry.limits.ymin &&
    y !== null &&
    y <= geometry.limits.ymax &&
    typeof geometry.xScale(x) !== 'undefined' &&
    typeof geometry.yScale(y) !== 'undefined';
  graph = {
    x: geometry.xScale(x) || 0,
    y: y == null ? 0 : geometry.yScale(y) || 0,
  };
  return { showCircle: showCircle, graph: graph };
}

function checkSpurGraphY(x: number, graphLine: GraphLine, geometry: GraphGeometry): number | null {
  const isInRange = function (cursorX: number | null, coordinateX: number | null): boolean {
    if (coordinateX == null || cursorX == null) return false;
    return cursorX >= coordinateX - 0.01 && cursorX <= coordinateX + 0.01;
  };

  for (const point of graphLine) {
    if (point && point[0]) {
      const realX = point[0];
      const xCoordinatePoint = typeof geometry.xScale(x) !== 'undefined' ? geometry.xScale(x) : null;
      const xCoordinateTarget = typeof geometry.xScale(realX) !== 'undefined' ? geometry.xScale(realX) : null;
      if (isInRange(xCoordinatePoint, xCoordinateTarget)) {
        return point[1] ? point[1] : null;
      }
    }
  }
  return null;
}

function interpolateSegmentY(x: number, previousPoint: GraphPoint, nextPoint: GraphPoint) {
  const nextPointX = nextPoint[0] as number; //Covered by guard clause from caller
  const previousPointX = previousPoint[0] as number;
  const nextPointY = nextPoint[1] as number;
  const previousPointY = previousPoint[1] as number;
  const m = (nextPointY - previousPointY) / (nextPointX - previousPointX);
  const b = previousPointY - m * previousPointX;
  const y3 = m * x + b;
  return y3;
}

function interpolateGraphY(x: number, graphLine: GraphLine, isLogGraph: boolean): number | null {
  const point = graphLine[0];
  if (typeof x !== 'undefined' && point && typeof point[0] !== 'undefined' && point[0] <= x && graphLine.length !== 0) {
    for (let i = 0; i < graphLine.length; i++) {
      if (graphLine[i] && graphLine[graphLine.length - 1]) {
        const nextPoint = graphLine[i] as GraphPoint; //Covered by guard clause above
        if (typeof nextPoint[0] !== 'undefined' && typeof nextPoint[1] !== 'undefined') {
          const nextPointX = nextPoint[0];
          const nextPointY = nextPoint[1];
          if (nextPointX > x && graphLine[i - 1]) {
            // x is right before here in the graph
            const previousPoint = graphLine[i - 1] as GraphPoint; //Covered by guard clause above
            if (typeof previousPoint[0] !== 'undefined' && typeof previousPoint[1] !== 'undefined') {
              const previousPointY = previousPoint[1];
              if (isLogGraph && (previousPointY === 0 || nextPointY === 0)) {
                return 0; // Hack for people passing zero on log graphs - always interpolate values adjacent to 0 as 0.
              } else {
                return interpolateSegmentY(x, previousPoint, nextPoint);
              }
            }
          }
        }
        // x is at the very end of the graph data
        const rightmostPoint = graphLine[graphLine.length - 1] as GraphPoint; //Covered by guard clause above
        if (typeof rightmostPoint[0] !== 'undefined' && typeof rightmostPoint[1] !== 'undefined') {
          const rightmostPointX = rightmostPoint[0];
          const rightmostPointY = rightmostPoint[1];
          if (rightmostPointX === x) {
            const previousPoint = graphLine[graphLine.length - 2] as GraphPoint;
            if (typeof previousPoint[1] !== 'undefined') {
              const previousPointY = previousPoint[1];
              if (isLogGraph && (previousPointY === 0 || rightmostPointY === 0)) {
                return 0; // Hack for people passing zero on log graphs - always interpolate values adjacent to 0 as 0.
              } else {
                return interpolateSegmentY(x, previousPoint, rightmostPoint);
              }
            }
          }
        }
      }
    }
    return null; // x is after the end of the graph data - no y value
  } else {
    return null; // x is after the end of the graph data - no y value
  }
}

export { getClosestDataX, getXValues, getYValues, getCircleCoordinates };
